import React, { useState, useEffect } from 'react';
import Header from "./Header";
import HeaderMobile from "./HeaderMobile";
import { translations } from "../translations";
import { useParams, useNavigate } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';
import GroupesElectrogenesDetail from './GroupesElectrogenesDetail';
import GroupesElectrogenesDetailMobile from './GroupesElectrogenesDetailMobile';
import MaterielElectriqueDetail from './MaterielElectriqueDetail';
import MaterielElectriqueDetailMobile from './MaterielElectriqueDetailMobile';
import ProductDetail from './ProductDetail';
import LoadingSpinner from './LoadingSpinner';

// Interface for product data
interface Product {
  id: string;
  category?: string;
  [key: string]: any;
}

const ProductDetailRouter: React.FC = () => {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Load language from localStorage or default to 'fr'
  const [language, setLanguage] = useState<'fr' | 'en' | 'ar'>(() => {
    const savedLanguage = localStorage.getItem('techyid-language') as 'fr' | 'en' | 'ar';
    return savedLanguage || 'fr';
  });
  const isRTL = language === 'ar';
  const t = translations[language];

  // Save language to localStorage whenever it changes
  const handleLanguageChange = (newLanguage: 'fr' | 'en' | 'ar') => {
    setLanguage(newLanguage);
    localStorage.setItem('techyid-language', newLanguage);
  };
  const [productCategory, setProductCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableProductIds, setAvailableProductIds] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const fetchProductCategory = async () => {
      console.log('��� ProductDetailRouter: Starting fetch for product ID:', productId);
      
      if (!productId) {
        console.log('❌ ProductDetailRouter: No product ID provided');
        setError('Product ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('��� ProductDetailRouter: Fetching product from Firebase...');
        
        // Get all available product IDs for debugging
        try {
          const allProducts = await FirebaseService.getAll('products');
          const allIds = allProducts?.map(p => p.id) || [];
          setAvailableProductIds(allIds);
          console.log('��� ProductDetailRouter: Available product IDs:', allIds);
        } catch (debugError) {
          console.log('⚠️ Could not fetch all products for debugging:', debugError);
        }
        
        const productData = await FirebaseService.getProduct(productId) as Product;
        console.log('��� ProductDetailRouter: Product data received:', productData);
        
        if (productData) {
          console.log('✅ ProductDetailRouter: Product found, category:', productData.category);
          setProductCategory(productData.category || '0');
        } else {
          console.log('❌ ProductDetailRouter: Product not found in database');
          setError(`Product with ID "${productId}" not found`);
        }
      } catch (err) {
        console.error('❌ ProductDetailRouter: Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProductCategory();
  }, [productId]);

  const handleBackToProducts = () => {
    navigate('/products');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '600px',
          width: '100%'
        }}>
          <h2 style={{ color: '#dc2626', marginBottom: '1rem' }}>
            Product Not Found
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            {error}
          </p>
          <p style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '1rem' }}>
            Product ID: {productId}
          </p>
          
          {availableProductIds.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                Available product IDs:
              </p>
              <div style={{ 
                maxHeight: '150px', 
                overflowY: 'auto', 
                backgroundColor: '#f9fafb', 
                padding: '1rem', 
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontFamily: 'monospace'
              }}>
                {availableProductIds.map((id, index) => (
                  <div key={index} style={{ marginBottom: '0.25rem' }}>
                    <a 
                      href={`/product/${id}`} 
                      style={{ color: '#dc2626', textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/product/${id}`);
                      }}
                    >
                      {id}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={handleBackToProducts}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              View All Products
            </button>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Route to Groupes Electrogènes detail page if category is '1'
  if (productCategory === '1') {
    return (
      <>
        {isMobile ? (
          <HeaderMobile language={language} setLanguage={setLanguage} t={t} isRTL={isRTL} showOnlyHome={true} searchTerm="" setSearchTerm={() => {}} selectedCategory="" setSelectedCategory={() => {}} viewMode="grid" setViewMode={() => {}} onClearFilters={() => {}} hasActiveFilters={false} />
        ) : (
          <Header language={language} setLanguage={setLanguage} t={t} isRTL={isRTL} showOnlyHome={true} />
        )}
        {isMobile ? (
          <GroupesElectrogenesDetailMobile />
        ) : (
          <GroupesElectrogenesDetail />
        )}
      </>
    );
  }

  // Route to Matériel Électrique detail page if category is '2'
  if (productCategory === '2') {
    return (
      <>
        {isMobile ? (
          <HeaderMobile language={language} setLanguage={setLanguage} t={t} isRTL={isRTL} showOnlyHome={true} searchTerm="" setSearchTerm={() => {}} selectedCategory="" setSelectedCategory={() => {}} viewMode="grid" setViewMode={() => {}} onClearFilters={() => {}} hasActiveFilters={false} />
        ) : (
          <Header language={language} setLanguage={setLanguage} t={t} isRTL={isRTL} showOnlyHome={true} />
        )}
        {isMobile ? (
          <MaterielElectriqueDetailMobile t={t} isRTL={isRTL} />
        ) : (
          <MaterielElectriqueDetail t={t} isRTL={isRTL} />
        )}
      </>
    );
  }

  // Route to general product detail page for other categories
  return (
    <>
      {isMobile ? (
        <HeaderMobile language={language} setLanguage={setLanguage} t={t} isRTL={isRTL} showOnlyHome={true} searchTerm="" setSearchTerm={() => {}} selectedCategory="" setSelectedCategory={() => {}} viewMode="grid" setViewMode={() => {}} onClearFilters={() => {}} hasActiveFilters={false} />
      ) : (
        <Header language={language} setLanguage={setLanguage} t={t} isRTL={isRTL} showOnlyHome={true} />
      )}
      <ProductDetail />
    </>
  );
};

export default ProductDetailRouter;
